import { createFileRoute, Link } from "@tanstack/react-router";
import { absUrl, canonicalUrl, leafCanonicalLink, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/marine-canvas-boat.jpg";
import heroPicture from "@/assets/marine-canvas-boat.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import shareImage from "@/assets/marine-canvas-boat.jpg";
import airboatMarsh from "@/assets/airboat-cover-marsh-console.jpg";

import offshoreTtop from "@/assets/offshore-cover-ttop-marina.jpg";


import guideBoatBimini from "@/assets/guide-boat-bimini-dock.jpg";
import blackBoatSide from "@/assets/boat-cover-black-side-trailer.jpg";
import blackBoatLakehouse from "@/assets/boat-cover-black-lakehouse.jpg";
import blackBoatRear from "@/assets/boat-cover-black-rear.jpg";
import blackBiminiMarina from "@/assets/black-canvas-bimini-top-boat-cover-marina.jpg";
import redPathfinderCover from "@/assets/red-pathfinder-storage-cover-lakeside.jpg";

import { BoatCoversExplorer } from "@/components/BoatCoversExplorer";
import { BoatCoverForm } from "@/components/BoatCoverForm";

const PAGE_PATH = "/boat-covers";

const GALLERY_IMAGES: Array<{ src: string; alt: string; caption: string }> = [
  
  { src: airboatMarsh, alt: "Texas fishing-guide air boat with low-profile aluminum hull, rear fan cage, raised guide platform and rod holders on a coastal marsh at golden hour", caption: "Fishing-guide air boat — Texas coastal marsh" },
  
  { src: offshoreTtop, alt: "Large offshore sportfishing boat at a Port Aransas marina with custom Sunbrella T-top canvas and helm cover", caption: "Offshore T-top canvas & helm cover — Port Aransas" },
  
  
  { src: guideBoatBimini, alt: "Coastal guide skiff at a fishing-lodge dock with custom bimini top and snap-on mooring cover in marine canvas", caption: "Guide skiff bimini top & snap-on mooring cover — Coastal Bend" },
  { src: blackBoatSide, alt: "Custom black heavy-duty marine canvas full storage cover fabricated by Texas Made Shade, fitted snug over a large offshore performance boat on a triple-axle trailer at a Central Texas lakehouse", caption: "Offshore performance boat — full storage cover on triple-axle trailer" },
  { src: blackBoatLakehouse, alt: "Custom black Sunbrella Marine full mooring cover built by Texas Made Shade tied down over an offshore boat parked beneath a Texas Hill Country lakehouse deck", caption: "Lakehouse storage cover — offshore boat under the deck" },
  { src: blackBoatRear, alt: "Rear-elevation view of a custom black marine canvas full storage cover fabricated by Texas Made Shade, sealed tight over the transom and outboards of an offshore boat on its trailer", caption: "Transom-tight rear cover — offshore boat & outboards" },
];

export const Route = createFileRoute("/boat-covers")({
  head: (ctx) => ({
    // Self-canonical: /boat-covers is its own leaf page.
    links: leafCanonicalLink(ctx, PAGE_PATH),
    meta: routeMeta(ctx, {
      title: "Custom Boat Covers & Bimini Tops Texas | Texas Made Shade",
      description:
        "Custom boat covers in Seguin, TX — air boat, offshore, guide boat, center console & bay boat covers, T-top canvas, bimini tops and mooring covers.",
      image: shareImage,
      imageAlt: "Custom marine canvas boat covers, T-tops and bimini tops by Texas Made Shade",
      socialTitle: "Custom Boat Covers — Air Boat, Offshore, Guide & Bay Boats | Texas Made Shade",
      socialDescription:
        "Air boat, offshore, guide boat, center console & bay boat covers, T-top canvas and bimini tops — built in Seguin, TX for the Texas Gulf Coast & Hill Country lakes.",
      keywords:
        "Boat Covers Corpus Christi, boat covers Texas, custom boat covers, air boat covers, offshore boat covers, guide boat covers, center console covers, bay boat covers, flats skiff covers, T-top canvas, bimini tops, mooring covers, marine canvas Corpus Christi, Sunbrella Marine, YKK marine zippers, Seguin TX, Port Aransas, Port O'Connor, Rockport, Galveston, Houston, Coastal Bend",
    }),
    scripts: [
      ...serviceHeadScripts({
        path: "/boat-covers",
        serviceName: "Boat Covers & Marine Canvas",
        breadcrumbName: "Boat Covers",
        description: "Custom marine canvas — boat covers, bimini tops, console covers and storage covers in Sunbrella Marine fabric.",
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "Custom Boat Cover Gallery — Texas Made Shade",
          url: canonicalUrl(PAGE_PATH),
          about: "Custom boat covers built in Seguin, Texas — air boat, offshore and guide boat covers, T-top canvas, bimini tops and mooring covers.",
          image: GALLERY_IMAGES.map((img) => ({
            "@type": "ImageObject",
            contentUrl: absUrl(img.src),
            url: absUrl(img.src),
            caption: img.caption,
            description: img.alt,
            creditText: "Texas Made Shade & Covers",
            creator: { "@type": "Organization", name: "Texas Made Shade & Covers", url: "https://texasmadeshade.com" },
            copyrightNotice: "© Texas Made Shade & Covers",
            license: canonicalUrl(PAGE_PATH),
            acquireLicensePage: canonicalUrl("/contact"),
            contentLocation: { "@type": "Place", name: "Seguin, Texas" },
          })),
        }),
      },
    ],
  }),
  component: () => (
    <ServicePage
      currentPath="/boat-covers"
      serviceName="Boat Covers"
      keywordOutline={[
        { id: "overview", label: "Custom Boat Covers & Marine Canvas in Texas" },
        { id: "why-us", label: "Why Choose Texas Made Shade for Boat Covers" },
        { id: "details", label: "Boat Cover Materials, Fit & Hardware" },
        { id: "applications", label: "Where We Pattern Boat Covers" },
        { id: "faqs", label: "Boat Cover & Marine Canvas FAQs" },
      ]}
      eyebrow="★ BOAT COVERS ★"
      title="Custom Boat Covers & Marine Canvas — Built to Fit Your Boat in Texas"
      intro="Custom boat covers and marine canvas in Texas — air boat, offshore, guide boat, bay boat and center console covers, T-top canvas, bimini tops and mooring covers. Patterned at the dock or in our Seguin shop with Sunbrella® Marine canvas, stainless hardware and YKK marine zippers for the Gulf Coast and Texas lakes."
      heroImage={heroPicture}
      features={[
        "Custom-patterned to your exact boat — not generic 'fits-most' boat covers",
        "Air boat, offshore, guide boat, bay boat, bass boat & center console covers",
        "T-top canvas, bimini tops, console & leaning-post covers",
        "Sunbrella® Marine canvas — UV, mildew & saltwater resistant",
        "Stainless steel snaps and YKK marine zippers — built for the Texas Gulf Coast",
        "Hurricane and high-wind tie-down systems available",
        "Marine canvas repair and re-stitch service for existing covers",
        "On-site patterning at your dock, slip or marina across Texas",
      ]}
      body={[
        {
          heading: "Air Boat, Offshore & Guide Boat Covers",
          text: "Mooring covers, storage covers, console covers, T-top canvas and bimini tops for bay boats, bass boats, jon boats, kayaks, air boats, offshore boats and guide boats — sewn from marine-grade fabric with hardware built for Texas coast humidity and salt.",
          bullets: [
            "Snap-fit mooring & full storage covers",
            "T-top canvas, leaning post and console covers",
            "Bimini tops with tubing and quick-release hardware",
            "30-gauge clear vinyl windows for visibility under tow",
            "Patterned in our Seguin shop or on-site at your slip",
          ],
        },
        { heading: "Mooring & Storage Covers", text: "Tight, water-shedding covers that protect your investment year-round on the Gulf, on the lake, or on the trailer. Built to handle Texas sun and Gulf storms." },
        { heading: "Console & Helm Covers", text: "Custom console covers, helm covers, and tackle station covers — protect your electronics and helm from sun, salt and dust." },
        { heading: "Full Enclosures", text: "Side curtains, drop curtains, and full vinyl enclosures for cruisers and offshore boats. Pattern-fit, no compromises." },
        { heading: "Repair & Re-Stitch", text: "Existing cover ripped or thread-rotted? We re-stitch with marine-grade thread and replace failed zippers and snaps." },
      ]}
      applications={[
        "Air Boats",
        "Offshore Boats",
        "Guide Boats",
        "Center Consoles",
        "Bay & Bass Boats",
        "Flats Skiffs",
        "Kayaks & Jon Boats",
        "Pontoons",
        "Sailboats",
        "PWC / Jet Skis",
      ]}
      faqs={[
        { q: "Do you come to my Texas dock or marina to pattern a custom boat cover?", a: "Yes. For most Texas Gulf Coast and Hill Country lake boats we pattern at your dock, slip or trailer in Corpus Christi, Rockport, Port Aransas and beyond. For boats brought to our Seguin shop, we pattern your custom boat cover in-house." },
        {
          q: "Can you pattern a custom boat cover at my Texas dock or boat lease?",
          a: "Usually on smaller boats the customer brings us the boat to our Seguin marine canvas shop so we have it to pattern. In some cases where the boat can not be transported — for example, a large yacht or offshore rig — we come to the Texas dock and pattern the custom boat cover on-site.",
          aJsx: (
            <p>
              Usually on smaller boats the customer brings the boat to{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin marine canvas shop</Link> so we can pattern in-house. For larger rigs that can&apos;t be transported — yachts and offshore boats — we travel to the dock and pattern on-site. See our full{" "}
              <Link to="/covers-tops" className="font-semibold text-navy-deep underline">marine canvas tops</Link> selection.
            </p>
          ),
        },
        {
          q: "Can you build T-top canvas, console covers and bimini tops for Texas bay boats?",
          a: "Yes — T-top canvas, console covers, leaning post covers, bimini tops, mooring covers and full storage covers for Texas bay boats. We carry a vast selection of marine fabrics, including Sunbrella® Marine, Top Gun® and Aqualon®, so we can match the look, weight and budget you need. We work with bay boat, bass boat, flats boat and offshore rigs across the Texas coast — Corpus Christi, Rockport, Port O'Connor, Port Aransas and Galveston.",
          aJsx: (
            <p>
              Yes — <Link to="/covers-tops" className="font-semibold text-navy-deep underline">T-top canvas, console covers, leaning post covers and bimini tops</Link>, plus mooring covers and full storage covers. We carry a vast selection of <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine fabrics</Link>, including Sunbrella® Marine, Top Gun® and Aqualon®. We work with bay boat, bass boat, flats boat and offshore rigs across the Texas coast — including{" "}
              <Link to="/corpus-christi" className="font-semibold text-navy-deep underline">Corpus Christi</Link>,{" "}
              <Link to="/houston" className="font-semibold text-navy-deep underline">Houston</Link> and the Gulf Coast.
            </p>
          ),
        },
        {
          q: "Do you build custom air boat covers for South Texas marshes and Hill Country lakes?",
          a: "Yes — we pattern custom air boat covers in Sunbrella® Marine and heavy marine vinyl, fitted around the fan cage, prop guard, console and rigging. Mooring covers, deck covers and console wraps are built in our Seguin shop for fan-cage air boats running South Texas marshes, the Coastal Bend and Hill Country lakes.",
          aJsx: (
            <p>
              Yes — we pattern <Link to="/boat-covers" hash="air-boat-covers" className="font-semibold text-navy-deep underline">custom air boat covers</Link> in Sunbrella® Marine and heavy marine vinyl, fitted around the fan cage, prop guard, console and rigging. Mooring covers, deck covers and console wraps are built in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin marine canvas shop</Link> for fan-cage air boats running South Texas marshes, the Coastal Bend and Hill Country lakes.
            </p>
          ),
        },
        {
          q: "What's the best marine fabric for an air boat mooring cover in South Texas?",
          a: "For South Texas air boats we typically recommend Sunbrella® Marine or Top Gun® for UV stability and breathability, or heavy vinyl-coated polyester when abrasion from cane, marsh grass and trailering is the bigger concern. We pair fabric with reinforced wear pads at the cage uprights and prop guard so the air boat cover holds up season after season.",
          aJsx: (
            <p>
              For air boats we typically recommend <Link to="/fabrics" className="font-semibold text-navy-deep underline">Sunbrella® Marine or Top Gun®</Link> for UV stability and breathability, or heavy vinyl-coated polyester when abrasion from cane, marsh grass and trailering is the bigger concern. We pair fabric with reinforced wear pads at the cage uprights and prop guard so the cover holds up season after season — see our full{" "}
              <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine fabric selection</Link>.
            </p>
          ),
        },
        {
          q: "Do you make custom offshore boat covers for Texas center consoles and sportfishing rigs?",
          a: "Yes — full storage and mooring covers, T-top canvas, helm and console covers, and leaning-post covers for Texas offshore boats and center consoles. We work with Gulf Coast anglers running out of Port Aransas, Port O'Connor, Rockport, Galveston and Corpus Christi, and pattern in our Seguin shop or on-site at the slip for boats too large to transport.",
          aJsx: (
            <p>
              Yes — full storage and mooring covers, <Link to="/covers-tops" className="font-semibold text-navy-deep underline">T-top canvas, helm and console covers</Link>, and leaning-post covers for{" "}
              <Link to="/boat-covers" hash="offshore-boat-covers" className="font-semibold text-navy-deep underline">offshore boats and center consoles</Link>. We work with Gulf Coast anglers running out of Port Aransas, Port O&apos;Connor, Rockport, Galveston and{" "}
              <Link to="/corpus-christi" className="font-semibold text-navy-deep underline">Corpus Christi</Link>, and pattern in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin shop</Link> or on-site at the slip for boats too large to transport.
            </p>
          ),
        },
        {
          q: "How do you protect rod holders, outriggers and antennas on a custom offshore boat cover?",
          a: "We pattern reinforced cutouts and storm flaps around rod holders, outriggers, antennas and T-top hardware so the offshore boat cover seals tight without trapping water or chafing the gel coat. Reinforced corners, rope channels and tie-down loops keep the cover snapped down through Gulf wind and trailering.",
        },
        {
          q: "Can you build guide boat covers and bimini tops for Texas bay & flats skiffs?",
          a: "Yes — bimini tops, console covers, leaning-post covers, poling-platform covers and snap-on mooring covers for Texas bay and flats guide boats. We build for Coastal Bend guides chasing redfish and trout, with marine canvas, YKK marine zippers and stainless hardware that holds up to daily salt-water use.",
          aJsx: (
            <p>
              Yes — bimini tops, console covers, leaning-post covers, poling-platform covers and snap-on mooring covers for{" "}
              <Link to="/boat-covers" hash="guide-boat-covers" className="font-semibold text-navy-deep underline">bay and flats guide boats</Link>. We build for Texas Coastal Bend guides chasing redfish and trout, with{" "}
              <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine canvas</Link>, YKK marine zippers and stainless hardware — explore our full{" "}
              <Link to="/covers-tops" className="font-semibold text-navy-deep underline">bimini top &amp; T-top canvas lineup</Link>.
            </p>
          ),
        },
        {
          q: "How long does a custom air boat, offshore or guide boat cover take to build in Texas?",
          a: "Most custom air boat, offshore and guide boat covers run 1–5 weeks depending on the queue and whether we're patterning in-shop or on-site at your Texas slip. Rush jobs are available with a rush fee — send us your boat, location and timeline for a written estimate.",
        },
        { q: "What marine fabric do you use for custom Texas boat covers?", a: "Sunbrella® Marine canvas. It's the industry standard for marine UV, mildew and water resistance, and we stand behind it for Texas Gulf and Hill Country lake use." },
        { q: "How long do custom Sunbrella Marine boat covers last in Texas?", a: "A well-built Sunbrella® Marine cover typically lasts 8–12 years in heavy Texas Gulf sun, longer with light use and proper care." },
        { q: "Do you service Texas Gulf Coast marinas — Corpus Christi, Rockport, Port Aransas?", a: "Yes — Corpus Christi, Rockport, Port Aransas, Port O'Connor, and the lake regions of Central Texas. Call +1 (830) 390-9588 to schedule patterning at your dock, slip or marina." },
      ]}
      cityCallouts={[
        { to: "/corpus-christi", city: "Corpus Christi", anchorText: "Boat covers in Corpus Christi & the Coastal Bend", blurb: "Custom marine canvas for center consoles, bay boats and offshore cruisers across the Coastal Bend." },
        { to: "/seguin", city: "Seguin", anchorText: "Boat covers patterned in our Seguin shop", blurb: "Bring your boat to our Seguin fabrication shop for in-house patterning, sewing, and fitting on Sunbrella Marine canvas." },
        { to: "/san-antonio", city: "San Antonio", anchorText: "Boat covers for San Antonio & area lakes", blurb: "Mooring covers, bimini tops, and storage covers for San Antonio boaters on Canyon Lake, Calaveras, and Medina." },
      ]}
      hideLetsTalkForm
      preContent={<BoatCoverForm />}
    >
      {/* ============ FEATURED BLACK CANVAS BIMINI ============ */}
      <section className="border-t-4 border-navy-deep bg-cream py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <figure className="sm:float-right sm:ml-6 sm:mb-3 sm:w-[46%] md:w-[42%] lg:w-[38%]">
            <img
              src={blackBiminiMarina}
              alt="Custom black Sunbrella marine canvas double-tier bimini top by Texas Made Shade installed on a Sea Ray boat docked at a Texas marina"
              width={1356}
              height={1808}
              className="block h-auto w-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <figcaption className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-deep/70">
              Black Sunbrella® bimini top · Sea Ray at the marina
            </figcaption>
          </figure>
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Featured Build</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Black Sunbrella® Bimini Top — Built for Texas Sun
          </h2>
          <p className="mt-4 text-lg text-navy-deep/80">
            This double-tier bimini top was patterned in our Seguin shop in heavy black Sunbrella® Marine canvas with
            stainless steel tubing and YKK marine zippers. Black canvas reads sharp against white gel-coat and shrugs
            off Gulf Coast sun, salt and afternoon thunderstorms — ideal for Sea Ray cruisers, center consoles and
            offshore rigs running Canyon Lake, Lake LBJ and the Coastal Bend.
          </p>
          <p className="mt-4 text-lg text-navy-deep/80">
            Every bimini we build starts with on-boat patterning so the canvas wraps tight to the frame with no flap or
            sag. Reinforced wear points, bound seams and stainless hardware mean the cover holds its shape season after
            season — whether it&apos;s tied down at the slip or trailered home for storage.
          </p>
          <div className="clear-both" />
        </div>
      </section>

      {/* ============ TABBED PRODUCT EXPLORER (Air / Offshore / Guide) ============ */}
      <BoatCoversExplorer />

      {/* ============ FEATURED RED STORAGE COVER ============ */}
      <section className="border-t-4 border-navy-deep bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <figure className="sm:float-left sm:mr-6 sm:mb-3 sm:w-[52%] md:w-[48%] lg:w-[44%]">
            <img
              src={redPathfinderCover}
              alt="Custom red Sunbrella marine canvas full storage cover by Texas Made Shade fitted tight over a Pathfinder bay boat on its trailer at a Texas lake"
              width={1456}
              height={1092}
              className="block h-auto w-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <figcaption className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-deep/70">
              Red marine canvas storage cover · Pathfinder bay boat
            </figcaption>
          </figure>
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Featured Build</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Red Marine Canvas Storage Cover — Pathfinder Bay Boat
          </h2>
          <p className="mt-4 text-lg text-navy-deep/80">
            This Pathfinder bay boat was patterned at the trailer in heavy red Sunbrella® Marine canvas with an
            internal support pole to shed water and keep the cover drum-tight. Bold red holds its color through Texas
            sun and stands out at the ramp — a favorite for Coastal Bend anglers and Hill Country lake owners who want
            a cover that protects and turns heads.
          </p>
          <p className="mt-4 text-lg text-navy-deep/80">
            Reinforced bow and transom corners, snug rope channels and bound seams keep the canvas snapped tight
            through trailering, crosswinds and afternoon thunderstorms. Every storage cover we build is patterned to
            your exact rig — center console, leaning post, T-top hardware and rod holders all worked into the
            template.
          </p>
          <div className="clear-both" />
        </div>
      </section>



      {/* ============ BOAT COVER PROJECT GALLERY ============ */}
      <section id="boat-cover-gallery" className="scroll-mt-24 border-t-4 border-navy-deep bg-cream py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Boat Cover Project Gallery</div>
          <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
            Custom Boat Covers We&apos;ve Built
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">
            Air boat covers, offshore boat covers, T-top canvas and guide boat builds — patterned and sewn in our
            Seguin shop for boats running the Texas Gulf Coast and Hill Country lakes.
          </p>
          <ul
            role="list"
            aria-label="Custom boat cover gallery"
            className="mt-10 grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6"
          >
            {GALLERY_IMAGES.map((img) => (
              <li key={img.src}>
                <figure className="group overflow-hidden border-2 border-navy-deep/10 bg-cream shadow-card">
                  <img
                    src={img.src}
                    alt={img.alt}
                    width={1200}
                    height={900}
                    loading="lazy"
                    decoding="async"
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="aspect-[4/3] w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                  <figcaption className="border-t-2 border-navy-deep/10 bg-white px-4 py-3">
                    <div className="font-display text-sm font-bold text-navy-deep">{img.caption}</div>
                  </figcaption>
                </figure>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </ServicePage>
  ),
});
